import type { ReactNode, JSX } from 'react';
import { useTenantFeatures } from '../../hooks/useTenantFeatures';
import { TenantFeature, TenantType } from '../../types/tenant.types';

interface FeatureGateProps {
  /**
   * The feature required to render the children
   */
  feature?: TenantFeature;

  /**
   * Multiple features - all must be present (AND logic)
   */
  features?: TenantFeature[];

  /**
   * Any of these features - at least one must be present (OR logic)
   */
  anyFeature?: TenantFeature[];

  /**
   * Specific tenant type required
   */
  tenantType?: TenantType;

  /**
   * Any of these tenant types
   */
  anyTenantType?: TenantType[];

  /**
   * Content to render when feature is available
   */
  children: ReactNode;

  /**
   * Optional fallback to render when feature is not available
   * Defaults to null (nothing rendered)
   */
  fallback?: ReactNode;
}

/**
 * FeatureGate Component
 *
 * Conditionally renders children based on tenant features and/or type.
 *
 * Usage:
 * ```tsx
 * // Single feature gate
 * <FeatureGate feature={TenantFeature.INCUBATORS}>
 *   <IncubatorsPage />
 * </FeatureGate>
 *
 * // Multiple features required (AND)
 * <FeatureGate features={[TenantFeature.SUBSCRIPTIONS, TenantFeature.ANALYTICS]}>
 *   <AdvancedDashboard />
 * </FeatureGate>
 *
 * // Any feature (OR)
 * <FeatureGate anyFeature={[TenantFeature.INCUBATORS, TenantFeature.USER_MANAGEMENT]}>
 *   <AdminPanel />
 * </FeatureGate>
 *
 * // Tenant type gate
 * <FeatureGate tenantType={TenantType.MASTER}>
 *   <TenantManagement />
 * </FeatureGate>
 *
 * // With fallback
 * <FeatureGate feature={TenantFeature.ANALYTICS} fallback={<UpgradePrompt />}>
 *   <AnalyticsPanel />
 * </FeatureGate>
 * ```
 */
export function FeatureGate({
  feature,
  features,
  anyFeature,
  tenantType,
  anyTenantType,
  children,
  fallback = null,
}: FeatureGateProps): JSX.Element | null {
  const { hasFeature, tenantType: currentType } = useTenantFeatures();

  // Check single feature
  if (feature && !hasFeature(feature)) {
    return <>{fallback}</>;
  }

  // Check multiple features (AND logic)
  if (features && features.length > 0) {
    const hasAllFeatures = features.every((f) => hasFeature(f));
    if (!hasAllFeatures) {
      return <>{fallback}</>;
    }
  }

  // Check any feature (OR logic)
  if (anyFeature && anyFeature.length > 0) {
    const hasAnyFeature = anyFeature.some((f) => hasFeature(f));
    if (!hasAnyFeature) {
      return <>{fallback}</>;
    }
  }

  // Check specific tenant type
  if (tenantType && currentType !== tenantType) {
    return <>{fallback}</>;
  }

  // Check any tenant type
  if (anyTenantType && anyTenantType.length > 0) {
    if (!anyTenantType.includes(currentType as TenantType)) {
      return <>{fallback}</>;
    }
  }

  return <>{children}</>;
}

export default FeatureGate;

